/**
 * Profile Settings Page
 *
 * Allows users to view and edit their profile information.
 * Shows login email as read-only, and allows editing of profile fields.
 * Company name field is only shown for investors.
 */

import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'

import { ApiError } from '@/common/api/client'
import { Text } from '@/common/components/text'
import { LoadingSpinner } from '@/common/components/loading-spinner'
import { useToast } from '@/common/components/toast'
import { useAuth } from '@/common/hooks/useAuth'
import { useUserProfile } from '@/common/hooks/useUserProfile'
import { useMarkets } from '@/common/hooks/useMarkets'
import { Input } from '@/common/components/input/Input'
import { Button } from '@/common/components/button'
import {
  ProfileForm,
  ChangePasswordForm,
  type ProfileFormData,
} from '@/features/settings/components'
import { useMyAgreementSignatures } from '@/features/agreement/hooks/useMyAgreementSignatures'
import { downloadAgreementPdf } from '@/features/agreement/utils/downloadAgreementPdf'
import { downloadExternalAgentAgreementPdf } from '@/features/agreement/utils/downloadExternalAgentAgreementPdf'
import { TermsContent } from '@/features/onboarding/content/termsContent'
import { ExternalAgentTermsContent } from '@/features/onboarding/content/externalAgentTermsContent'

import * as styles from './profile.css'

export const Route = createFileRoute({
  component: ProfileSettingsPage,
})

function ProfileSettingsPage() {
  const { user, roles } = useAuth()
  const { data, isLoading, error, updateProfile, recordSmsConsent, resetProductTour, resetTraining } = useUserProfile()
  const { showSuccessToast, showErrorToast } = useToast()
  const { groupedMarketOptions } = useMarkets()
  const navigate = useNavigate()
  const [isSaving, setIsSaving] = useState(false)
  const [isResettingTour, setIsResettingTour] = useState(false)
  const [isResettingTraining, setIsResettingTraining] = useState(false)

  // Check if user is an investor (for showing company name and markets fields)
  const isInvestor = roles.includes('investor')
  const isExternalAgent = data?.profile?.agent_type === 'external'
  const { data: agreementData } = useMyAgreementSignatures()
  // Filter the user's signature list down to the agreement type relevant to
  // each settings card. Investor signatures use date-only versions
  // (e.g. `2026-04-06`); external-agent signatures use the prefixed scheme
  // (`external-agent-…`) — see API constants.
  const investorSignatures = agreementData?.signatures.filter(
    (s) => !s.agreement_version.startsWith('external-agent-'),
  )
  const externalAgentSignatures = agreementData?.signatures.filter(
    (s) => s.agreement_version.startsWith('external-agent-'),
  )

  /**
   * Handle replay product tour
   */
  const handleReplayTour = async () => {
    setIsResettingTour(true)
    try {
      await resetProductTour()
      navigate({ to: '/properties' })
    } catch (err) {
      console.error('Failed to reset product tour:', err)
      showErrorToast('Failed to start tour. Please try again.')
    } finally {
      setIsResettingTour(false)
    }
  }

  /**
   * MRG-281: Reset external-agent training so the guided tour replays.
   * Resets `training_completed_at` on the server, then navigates to
   * `/properties` — the global `useAgentTrainingTour` hook detects the
   * flipped server flag and auto-launches the tour from phase 1.
   */
  const handleRevisitTraining = async () => {
    setIsResettingTraining(true)
    try {
      await resetTraining()
      navigate({ to: '/properties' })
    } catch (err) {
      console.error('Failed to reset training:', err)
      showErrorToast('Failed to reset training. Please try again.')
    } finally {
      setIsResettingTraining(false)
    }
  }

  /**
   * Handle form submission
   */
  const handleSubmit = async (formData: ProfileFormData) => {
    setIsSaving(true)
    try {
      let saved
      try {
        saved = await updateProfile({
          first_name: formData.first_name,
          last_name: formData.last_name,
          public_email: formData.public_email,
          public_phone: formData.public_phone,
          company_name: formData.company_name,
          markets: formData.markets,
        })
      } catch (err) {
        console.error('Failed to update profile:', err)
        showErrorToast('Failed to update profile. Please try again.')
        return
      }
      // Compare with the saved state (a phone change revokes consent) so only real changes are recorded
      const smsConsent = formData.sms_consent ?? false
      if (isInvestor && smsConsent !== (saved.sms_consent ?? false)) {
        try {
          await recordSmsConsent(smsConsent)
        } catch (err) {
          console.error('Failed to record SMS consent:', err)
          // 409 = the SMS wording changed since this page loaded
          showErrorToast(
            err instanceof ApiError && err.status === 409
              ? `Profile saved. ${err.message}`
              : "Profile saved, but we couldn't update your text message preference. Please try again.",
          )
          return
        }
      }
      showSuccessToast('Profile updated successfully')
    } finally {
      setIsSaving(false)
    }
  }

  // Loading state
  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <LoadingSpinner size="lg" />
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <Text as="h2" variant="bodyLg" weight="bold">
            Unable to Load Profile
          </Text>
          <Text variant="body" color="muted">
            {error}
          </Text>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Text as="h1" variant="h4" weight="bold" className={styles.title}>
          Profile Settings
        </Text>
        <Text variant="body" className={styles.subtitle}>
          Manage your profile information and contact details.
        </Text>
      </div>

      <div className={styles.card}>
        <Input label="Login Email" value={user?.email || data?.email} disabled />

        <div className={styles.divider} />

        <ProfileForm
          initialValues={data?.profile}
          loginEmail={data?.email}
          showCompanyName={isInvestor}
          showMarkets={isInvestor}
          showSmsConsent={isInvestor}
          marketOptions={groupedMarketOptions}
          userRole={isInvestor ? 'investor' : 'agent'}
          submitLabel="Save Changes"
          isSubmitting={isSaving}
          onSubmit={handleSubmit}
        />
      </div>

      {/* Security Section */}
      <div className={`${styles.card} ${styles.cardSpacing}`}>
        <div className={styles.sectionHeader}>
          <Text as="h2" variant="bodyLg" weight="bold" className={styles.sectionTitle}>
            Security
          </Text>
          <Text variant="bodySm" className={styles.sectionDescription}>
            Manage your password and account security.
          </Text>
        </div>

        <ChangePasswordForm />
      </div>

      {/* Investor Agreement Section - investors only */}
      {isInvestor && (
        <div className={`${styles.card} ${styles.cardSpacing}`}>
          <div className={styles.sectionHeader}>
            <Text as="h2" variant="bodyLg" weight="bold" className={styles.sectionTitle}>
              Investor Agreement
            </Text>
            <Text variant="bodySm" className={styles.sectionDescription}>
              Review the terms of your investor agreement and download a signed copy.
            </Text>
          </div>

          {agreementData?.current_version && investorSignatures && investorSignatures.length > 0 ? (
            <>
              <Text variant="bodySm" color="muted" style={{ marginBottom: 12 }}>
                Version {agreementData.current_version} &mdash; Signed on{' '}
                {new Date(investorSignatures[0].signed_at).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </Text>
              <Button
                variant="secondary"
                onClick={() => downloadAgreementPdf(investorSignatures[0])}
                style={{ marginBottom: 16 }}
              >
                Download PDF
              </Button>
            </>
          ) : (
            <Text variant="bodySm" color="muted" style={{ marginBottom: 16 }}>
              No agreement signed.
            </Text>
          )}

          <div className={styles.termsContainer}>
            <TermsContent />
          </div>
        </div>
      )}

      {/* External-Agent Marketplace Agreement Section (MRG-281) — external agents only */}
      {isExternalAgent && (
        <div className={`${styles.card} ${styles.cardSpacing}`}>
          <div className={styles.sectionHeader}>
            <Text as="h2" variant="bodyLg" weight="bold" className={styles.sectionTitle}>
              Marketplace Agent Agreement
            </Text>
            <Text variant="bodySm" className={styles.sectionDescription}>
              Review the terms of your Marketplace Agent Agreement and download a signed copy.
            </Text>
          </div>

          {agreementData?.current_external_agent_terms_version
            && externalAgentSignatures
            && externalAgentSignatures.length > 0 ? (
            <>
              <Text variant="bodySm" color="muted" style={{ marginBottom: 12 }}>
                Version {agreementData.current_external_agent_terms_version} &mdash; Signed on{' '}
                {new Date(externalAgentSignatures[0].signed_at).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </Text>
              <Button
                variant="secondary"
                onClick={() => downloadExternalAgentAgreementPdf(externalAgentSignatures[0])}
                style={{ marginBottom: 16 }}
              >
                Download PDF
              </Button>
            </>
          ) : (
            <Text variant="bodySm" color="muted" style={{ marginBottom: 16 }}>
              No agreement signed.
            </Text>
          )}

          <div className={styles.termsContainer}>
            <ExternalAgentTermsContent />
          </div>
        </div>
      )}

      {/* Training Section (MRG-281) — external agents only */}
      {isExternalAgent && (
        <div className={`${styles.card} ${styles.cardSpacing}`}>
          <div className={styles.sectionHeader}>
            <Text as="h2" variant="bodyLg" weight="bold" className={styles.sectionTitle}>
              Marketplace Training
            </Text>
            <Text variant="bodySm" className={styles.sectionDescription}>
              Revisit the Marketplace training modules anytime. Resetting clears your completion
              status so you can step through each module again.
            </Text>
          </div>
          <Button
            variant="secondary"
            onClick={handleRevisitTraining}
            disabled={isResettingTraining}
          >
            {isResettingTraining ? 'Resetting…' : 'Revisit Training'}
          </Button>
        </div>
      )}

      {/* Product Tour Section - investors only */}
      {isInvestor && (
        <div className={`${styles.card} ${styles.cardSpacing}`}>
          <div className={styles.sectionHeader}>
            <Text as="h2" variant="bodyLg" weight="bold" className={styles.sectionTitle}>
              Product Tour
            </Text>
            <Text variant="bodySm" className={styles.sectionDescription}>
              Replay the guided tour to learn about the platform features.
            </Text>
          </div>
          <Button
            variant="secondary"
            onClick={handleReplayTour}
            disabled={isResettingTour}
          >
            {isResettingTour ? 'Starting Tour...' : 'Replay Product Tour'}
          </Button>
        </div>
      )}
    </div>
  )
}
